import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { schoolContext, setUnauthorizedHandler, tokenStorage } from '@/core/api';
import { normalizeRole, useSession, type Membership } from '@/stores/session';
import { getMySchools } from '@/features/schools/api';
import type { MySchoolDto } from '@/features/schools/types';
import * as api from './api';
import { AuthContext, type AuthStatus } from './authContext';
import type { LoginInput, RegisterInput } from './types';

/** Drop memberships whose role the UI doesn't know (deny-by-default). */
const toMemberships = (rows: MySchoolDto[]): Membership[] =>
  rows.flatMap(row => {
    const role = normalizeRole(row.role);
    return role ? [{ school: row.school, role }] : [];
  });

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useSession(s => s.setSession);
  const clearSession = useSession(s => s.clear);

  // Start in "loading" only if we have a refresh token to bootstrap from.
  const [status, setStatus] = useState<AuthStatus>(() =>
    tokenStorage.getRefresh() ? 'loading' : 'unauthenticated'
  );

  // Bootstrap order: fetch my schools → pick active (persisted slug, else
  // first) → set the X-School header → fetch the current user. The access
  // token is restored via 401→refresh if needed.
  const loadSession = useCallback(async () => {
    const memberships = toMemberships(await getMySchools());
    const saved = useSession.getState().activeSchoolSlug; // persisted across reloads
    const active = memberships.find(m => m.school.slug === saved) ?? memberships[0] ?? null;
    const activeSlug = active?.school.slug ?? null;
    schoolContext.set(activeSlug); // set before current-user so scoping is correct
    const user = await api.getCurrentUser();
    setSession({ user, memberships, activeSchoolSlug: activeSlug });
  }, [setSession]);

  const signIn = useCallback(
    async (input: LoginInput) => {
      const res = await api.login(input);
      const { access, refresh } = api.tokensFrom(res);
      tokenStorage.set(access, refresh);
      await loadSession();
      setStatus('authenticated');
    },
    [loadSession]
  );

  const signUp = useCallback(
    async (input: RegisterInput) => {
      const res = await api.register(input);
      const { access, refresh } = api.tokensFrom(res);
      if (!access) {
        // Mandatory email verification: account created, no tokens yet.
        return false;
      }
      tokenStorage.set(access, refresh);
      await loadSession();
      setStatus('authenticated');
      return true;
    },
    [loadSession]
  );

  const signOut = useCallback(async () => {
    await api.logout();
    tokenStorage.clear();
    clearSession();
    qc.clear();
    setStatus('unauthenticated');
    navigate('/login');
  }, [clearSession, navigate, qc]);

  // Re-fetch schools + user without changing auth status — used after
  // CreateSchool so the new membership lands in the session store.
  const refreshSession = useCallback(async () => {
    await loadSession();
  }, [loadSession]);

  // Bootstrap once on mount when a refresh token exists.
  useEffect(() => {
    if (status !== 'loading') return;
    let cancelled = false;
    void (async () => {
      try {
        await loadSession();
        if (!cancelled) setStatus('authenticated');
      } catch {
        if (!cancelled) {
          tokenStorage.clear();
          clearSession();
          setStatus('unauthenticated');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, loadSession, clearSession]);

  // An unrecoverable 401 (refresh failed) → clean sign-out + redirect.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      tokenStorage.clear();
      clearSession();
      setStatus('unauthenticated');
      navigate('/login');
    });
  }, [clearSession, navigate]);

  const value = useMemo(
    () => ({ status, signIn, signUp, signOut, refreshSession }),
    [status, signIn, signUp, signOut, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
